import { Car } from 'lucide-react';
import { cn } from '@bia-auction/ui';
import { useCallback, useEffect, useState } from 'react';
import { useMediaSrc } from './MediaImg';

/**
 * Cover thumbnail for a vehicle, with a deliberate placeholder rather than an empty box: a
 * blank grey rectangle in a list of cars reads as a broken image, not as "no photo yet".
 *
 * There is **no thumbnail variant** to ask for — only browser uploads are canvas-downscaled to
 * 1280px and mirrored photos are stored exactly as fetched (ADR 0025: no server-side image
 * processing) — so sizing is CSS + `object-cover`, never a `-tn` URL. A 25-row page can
 * therefore pull 25 full-size photos, which is why the wait is *shown* rather than hidden: the
 * box holds its size (no reflow as rows resolve), shimmers while the bytes are in flight, and
 * fades the photo in when it lands. A failed load falls back to the same icon as "no photo" —
 * a broken-image glyph tells an operator nothing they can act on.
 */
export function VehicleThumb({
  url,
  plate,
  className,
}: {
  url?: string | null;
  plate?: string | null;
  className?: string;
}) {
  // Through `useMediaSrc`, so an expired access token recovers instead of falling back to the
  // "no photo" icon (ADR 0027). The two states below compose with it for free: a successful
  // refresh changes the token in `src`, and the effect keyed on `src` clears `failed` — so the
  // icon shown while the retry is in flight is replaced by the photo when it lands, and stays
  // only if the refresh produced nothing.
  const { src, onError: recoverSrc, onLoad: armRecovery } = useMediaSrc(url);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // A new `src` (page change, cover replaced) restarts the cycle — otherwise the previous
  // photo's `loaded` would show the next one's shimmer-free empty box.
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  // A cached image can finish decoding before React attaches `onLoad`, which would leave the
  // shimmer up forever. The ref catches that already-`complete` case on mount.
  const settleIfComplete = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete) setLoaded(img.naturalWidth > 0);
  }, []);

  const base = 'relative shrink-0 overflow-hidden rounded-md border bg-muted';

  if (!src || failed) {
    return (
      <div
        className={cn(base, 'flex items-center justify-center text-muted-foreground', className)}
        aria-hidden
      >
        <Car className="h-4 w-4" />
      </div>
    );
  }

  return (
    <div className={cn(base, className)}>
      {!loaded && <div aria-hidden className="absolute inset-0 animate-pulse bg-muted" />}
      <img
        ref={settleIfComplete}
        src={src}
        alt={plate ?? ''}
        loading="lazy"
        decoding="async"
        onLoad={() => {
          setLoaded(true);
          armRecovery();
        }}
        onError={() => {
          setFailed(true);
          recoverSrc();
        }}
        className={cn(
          'h-full w-full object-cover transition-opacity duration-200',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}
